import { createSlice } from '@reduxjs/toolkit'
import { isEqual } from 'lodash'
import { useSelector } from 'react-redux'

import { AnimalType } from '#/store/ducks/context/types/Animal'
import { BatchType } from '#/store/ducks/context/types/Batch'
import { CComType } from '#/store/ducks/context/types/CCom'
import { CEnvType } from '#/store/ducks/context/types/CEnv'
import { CompanyProjectType } from '#/store/ducks/context/types/CompanyProject'
import { CTechType } from '#/store/ducks/context/types/CTech'
import { ImportData } from '#/store/ducks/context/types/Import'
import { ListType } from '#/store/ducks/context/types/List'
import { ProductionType } from '#/store/ducks/context/types/Production'
import { ProtocolType } from '#/store/ducks/context/types/Protocol'
import { SanitaryProtocolType } from '#/store/ducks/context/types/SanitaryProtocol'
import { SanitaryProtocolModelType } from '#/store/ducks/context/types/SanitaryProtocolModel'
import { SemenType } from '#/store/ducks/context/types/Semen'
import { ThiType } from '#/store/ducks/context/types/Thi'

type ContextState = {
  animal: AnimalType | Record<string, never>
  batch: BatchType | Record<string, never>
  ccom: CComType | Record<string, never>
  cenv: CEnvType | Record<string, never>
  companyProject: CompanyProjectType | Record<string, never>
  ctech: CTechType | Record<string, never>
  import: ImportData | Record<string, never>
  list: ListType | Record<string, never>
  production: ProductionType | Record<string, never>
  protocol: ProtocolType | Record<string, never>
  sanitaryProtocol: SanitaryProtocolType | Record<string, never>
  sanitaryProtocolModel: SanitaryProtocolModelType | Record<string, never>
  semen: SemenType | Record<string, never>
  thi: ThiType | Record<string, never>
}

const defaultState: ContextState = {
  animal: {},
  batch: {},
  ccom: {},
  cenv: {},
  companyProject: {},
  ctech: {},
  import: {},
  list: {},
  production: {},
  protocol: {},
  sanitaryProtocol: {},
  sanitaryProtocolModel: {},
  semen: {},
  thi: {},
}

export const contextSlice = createSlice({
  initialState: defaultState,
  name: 'context',
  reducers: {
    resetAnimalContext(state) {
      state.animal = {}
    },
    resetBatchContext(state) {
      state.batch = {}
    },
    resetCComContext(state) {
      state.ccom = {}
    },
    resetCEnvContext(state) {
      state.cenv = {}
    },
    resetCompanyProjectContext(state) {
      state.companyProject = {}
    },
    resetCTechContext(state) {
      state.ctech = {}
    },
    resetImportContext(state) {
      state.import = {}
    },
    resetListContext(state) {
      state.list = {}
    },
    resetProductionContext(state) {
      state.production = {}
    },
    resetProtocolContext(state) {
      state.protocol = {}
    },
    resetSanitaryProtocolContext(state) {
      state.sanitaryProtocol = {}
    },
    resetSanitaryProtocolModelContext(state) {
      state.sanitaryProtocolModel = {}
    },
    resetSemenContext(state) {
      state.semen = {}
    },
    resetThiContext(state) {
      state.thi = {}
    },
    setAnimalContext(state, { payload }) {
      state.animal = payload
    },
    setBatchContext(state, { payload }) {
      state.batch = payload
    },
    setCComContext(state, { payload }) {
      state.ccom = payload
    },
    setCEnvContext(state, { payload }) {
      state.cenv = payload
    },
    setCompanyProjectContext(state, { payload }) {
      state.companyProject = payload
    },
    setCTechContext(state, { payload }) {
      state.ctech = payload
    },
    setImportContext(state, { payload }) {
      state.import = payload
    },
    setListContext(state, { payload }) {
      state.list = payload
    },
    setProductionContext(state, { payload }) {
      state.production = payload
    },
    setProtocolContext(state, { payload }) {
      state.protocol = payload
    },
    setSanitaryProtocolContext(state, { payload }) {
      state.sanitaryProtocol = payload
    },
    setSanitaryProtocolModelContext(state, { payload }) {
      state.sanitaryProtocolModel = payload
    },
    setSemenContext(state, { payload }) {
      state.semen = payload
    },
    setThiContext(state, { payload }) {
      state.thi = payload
    },
  },
  selectors: {
    getAnimalContext: state => state.animal,
    getBatchContext: state => state.batch,
    getCComContext: state => state.ccom,
    getCEnvContext: state => state.cenv,
    getCompanyProjectContext: state => state.companyProject,
    getCTechContext: state => state.ctech,
    getImportContext: state => state.import,
    getListContext: state => state.list,
    getProductionContext: state => state.production,
    getProtocolContext: state => state.protocol,
    getSanitaryProtocolContext: state => state.sanitaryProtocol,
    getSanitaryProtocolModelContext: state => state.sanitaryProtocolModel,
    getSemenContext: state => state.semen,
    getThiContext: state => state.thi,
  },
})

export const {
  actions: {
    resetAnimalContext,
    resetBatchContext,
    resetCComContext,
    resetCEnvContext,
    resetCompanyProjectContext,
    resetCTechContext,
    resetImportContext,
    resetListContext,
    resetProductionContext,
    resetProtocolContext,
    resetSanitaryProtocolContext,
    resetSanitaryProtocolModelContext,
    resetSemenContext,
    resetThiContext,
    setAnimalContext,
    setBatchContext,
    setCComContext,
    setCEnvContext,
    setCompanyProjectContext,
    setCTechContext,
    setImportContext,
    setListContext,
    setProductionContext,
    setProtocolContext,
    setSanitaryProtocolContext,
    setSanitaryProtocolModelContext,
    setSemenContext,
    setThiContext,
  },
  selectors: {
    getAnimalContext,
    getBatchContext,
    getCComContext,
    getCEnvContext,
    getCompanyProjectContext,
    getCTechContext,
    getImportContext,
    getListContext,
    getProductionContext,
    getProtocolContext,
    getSanitaryProtocolContext,
    getSanitaryProtocolModelContext,
    getSemenContext,
    getThiContext,
  },
} = contextSlice

export const useAnimal = (): AnimalType => useSelector(getAnimalContext, isEqual) as AnimalType
export const useBatch = (): BatchType => useSelector(getBatchContext, isEqual) as BatchType
export const useCCom = (): CComType => useSelector(getCComContext, isEqual) as CComType
export const useCEnv = (): CEnvType => useSelector(getCEnvContext, isEqual) as CEnvType
export const useCompanyProject = (): CompanyProjectType =>
  useSelector(getCompanyProjectContext, isEqual) as CompanyProjectType
export const useCTech = (): CTechType => useSelector(getCTechContext, isEqual) as CTechType
export const useImport = (): ImportData => useSelector(getImportContext, isEqual) as ImportData
export const useList = (): ListType => useSelector(getListContext, isEqual) as ListType
export const useProduction = (): ProductionType =>
  useSelector(getProductionContext, isEqual) as ProductionType
export const useProtocol = (): ProtocolType =>
  useSelector(getProtocolContext, isEqual) as ProtocolType
export const useSanitaryProtocol = (): SanitaryProtocolType =>
  useSelector(getSanitaryProtocolContext, isEqual) as SanitaryProtocolType
export const useSanitaryProtocolModel = (): SanitaryProtocolModelType =>
  useSelector(getSanitaryProtocolModelContext, isEqual) as SanitaryProtocolModelType
export const useSemen = (): SemenType => useSelector(getSemenContext, isEqual) as SemenType
export const useThi = (): ThiType => useSelector(getThiContext, isEqual) as ThiType

export const useCTechCode = () => useCTech()?.code
export const useProtocolSlug = () => useProtocol()?.slug
export const useSanitaryProtocolSlug = () => useSanitaryProtocol()?.id
export const useSanitaryProtocolModelSlug = () => useSanitaryProtocolModel()?.id

export default contextSlice.reducer
